import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Gift, 
  FileText, 
  Building2, 
  MapPin, 
  Users,
  Calculator,
  CheckCircle2,
  ArrowRight,
  AlertCircle,
  Info
} from 'lucide-react';

const AyudasSubvenciones = () => {
  const ayudas = [
    {
      icon: FileText,
      title: 'Deducción en el IRPF',
      porcentaje: 'Hasta 40%',
      description: 'Deducción fiscal en tu declaración de la renta por actuaciones que mejoren la eficiencia energética de tu vivienda habitual.',
      requisitos: [
        'Reducir al menos un 30% el consumo de energía primaria no renovable',
        'Alcanzar una calificación energética A o B',
        'Aplicable a vivienda habitual',
        'Certificado energético antes y después de la obra'
      ],
      detalles: 'Esta deducción se aplica sobre las cantidades invertidas en la mejora de la eficiencia energética, con un límite que varía según la reducción conseguida. Es una ayuda directa en tu declaración del IRPF.',
      vigencia: 'Vigente hasta 31 de diciembre de 2024 (pendiente de prórroga)'
    },
    {
      icon: Building2,
      title: 'Bonificaciones del IBI',
      porcentaje: 'Hasta 50%',
      description: 'Bonificación en el Impuesto sobre Bienes Inmuebles aplicada por los ayuntamientos durante varios años.',
      requisitos: [
        'Instalación de paneles solares en la vivienda',
        'Solicitud ante el ayuntamiento correspondiente',
        'Cumplir requisitos específicos del municipio',
        'Duración de 3 a 5 años según municipio'
      ],
      detalles: 'Cada ayuntamiento establece su propio porcentaje de bonificación y duración. Las bonificaciones más habituales oscilan entre el 25% y el 50% durante 3 a 5 años. Consulta con tu ayuntamiento las condiciones específicas.',
      vigencia: 'Permanente (según ordenanza municipal)'
    },
    {
      icon: Building2,
      title: 'Bonificaciones del ICIO',
      porcentaje: 'Hasta 95%',
      description: 'Bonificación en el Impuesto sobre Construcciones, Instalaciones y Obras aplicada al realizar la instalación solar.',
      requisitos: [
        'Solicitud previa a la instalación',
        'Presentación junto con licencia de obras',
        'Cumplir requisitos del ayuntamiento',
        'Pago único en el momento de la instalación'
      ],
      detalles: 'Esta bonificación se aplica al iniciar la obra y supone un ahorro inmediato en el coste de la licencia de instalación. Los porcentajes varían mucho entre municipios, siendo habitual entre el 50% y el 95%.',
      vigencia: 'Permanente (según ordenanza municipal)'
    },
    {
      icon: MapPin,
      title: 'Subvenciones Autonómicas',
      porcentaje: '20% - 50%',
      description: 'Ayudas directas gestionadas por las comunidades autónomas para fomentar el autoconsumo energético.',
      requisitos: [
        'Instalación de sistemas de autoconsumo',
        'Cumplir potencia mínima y máxima establecida',
        'Solicitud dentro de plazo de convocatoria',
        'Justificación de la inversión realizada'
      ],
      detalles: 'Las comunidades autónomas publican convocatorias periódicas con presupuesto limitado. El importe de la subvención depende de la potencia instalada (€/kWp) y puede variar entre el 20% y el 50% del coste total. Es importante solicitar la ayuda antes de realizar la instalación.',
      vigencia: 'Convocatorias periódicas (consultar plazo activo)'
    },
    {
      icon: Users,
      title: 'Autoconsumo Colectivo',
      porcentaje: 'Incentivos Especiales',
      description: 'Ayudas específicas para instalaciones compartidas en comunidades de vecinos, empresas o proyectos colectivos.',
      requisitos: [
        'Instalación para múltiples usuarios',
        'Acuerdo de reparto de energía',
        'Constitución legal del grupo de autoconsumo',
        'Mayor cuantía de subvención por kWp instalado'
      ],
      detalles: 'Las nuevas políticas energéticas están fomentando especialmente las instalaciones compartidas. Estas reciben mayor cuantía de ayuda y permiten aprovechar mejor los espacios comunes. Ideal para comunidades de vecinos y polígonos industriales.',
      vigencia: 'Programa prioritario en todas las convocatorias'
    }
  ];

  const pasos = [
    {
      numero: '1',
      titulo: 'Consulta Inicial',
      descripcion: 'Analizamos tu caso y te informamos de todas las ayudas disponibles en tu municipio y comunidad autónoma.'
    },
    {
      numero: '2',
      titulo: 'Documentación',
      descripcion: 'Te ayudamos a reunir toda la documentación necesaria para solicitar las ayudas (certificados, proyectos, etc.).'
    },
    {
      numero: '3',
      titulo: 'Tramitación',
      descripcion: 'Gestionamos todas las solicitudes ante los organismos correspondientes sin coste adicional.'
    },
    {
      numero: '4',
      titulo: 'Seguimiento',
      descripcion: 'Hacemos seguimiento de tus expedientes hasta la concesión y cobro de las ayudas.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-nubia-gold-soft/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Gift className="w-10 h-10 text-nubia-gold-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Ayudas y Subvenciones para Placas Solares
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Información actualizada sobre todas las ayudas disponibles en España para tu instalación solar
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-nubia-green-primary/5 border-l-4 border-nubia-green-primary rounded-lg p-8">
              <div className="flex items-start gap-4">
                <Info className="w-8 h-8 text-nubia-green-primary flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold text-nubia-green-primary mb-4">
                    Reduce tu Inversión hasta un 40%
                  </h2>
                  <p className="text-lg text-nubia-text-dark/70 leading-relaxed mb-4">
                    Instalar placas solares en España puede beneficiarse de diferentes incentivos públicos 
                    que reducen significativamente la inversión inicial. Existen ayudas a nivel estatal, 
                    autonómico y municipal que pueden combinarse para maximizar tu ahorro.
                  </p>
                  <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
                    En Nubia Solar te asesoramos sobre todas las ayudas disponibles en tu zona y nos 
                    encargamos de gestionar toda la tramitación sin coste adicional.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ayudas Disponibles */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Ayudas Disponibles en España
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Estas son las principales ayudas que puedes solicitar para tu instalación solar
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto space-y-8">
            {ayudas.map((ayuda, index) => {
              const Icon = ayuda.icon;
              return (
                <Card key={index} className="nubia-shadow border-2 border-nubia-gray-light bg-white">
                  <CardHeader>
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-8 h-8 text-nubia-gold-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <CardTitle className="text-3xl text-nubia-green-primary">{ayuda.title}</CardTitle>
                          <div className="text-right flex-shrink-0">
                            <div className="text-3xl font-bold text-nubia-gold-primary">{ayuda.porcentaje}</div>
                            <div className="text-sm text-nubia-text-dark/60">Ahorro</div>
                          </div>
                        </div>
                        <CardDescription className="text-lg">{ayuda.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-nubia-green-primary mb-4 text-lg">Requisitos:</h4>
                        <div className="space-y-3">
                          {ayuda.requisitos.map((req, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <CheckCircle2 className="w-5 h-5 text-nubia-green-support mt-0.5 flex-shrink-0" />
                              <span className="text-nubia-text-dark/70">{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-nubia-green-primary mb-4 text-lg">Detalles:</h4>
                        <p className="text-nubia-text-dark/70 leading-relaxed mb-4">{ayuda.detalles}</p>
                        <div className="bg-nubia-gold-soft/10 border border-nubia-gold-primary/30 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-nubia-gold-primary flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="font-medium text-nubia-green-primary mb-1">Vigencia</div>
                              <div className="text-sm text-nubia-text-dark/70">{ayuda.vigencia}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Como Tramitamos las Ayudas */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
                Cómo Gestionamos tus Ayudas
              </h2>
              <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
                En Nubia Solar nos encargamos de todo el proceso de tramitación sin coste adicional
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {pasos.map((paso, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-nubia-gold-primary text-nubia-green-primary rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                    {paso.numero}
                  </div>
                  <h3 className="text-xl font-bold text-nubia-green-primary mb-3">{paso.titulo}</h3>
                  <p className="text-nubia-text-dark/70 leading-relaxed">{paso.descripcion}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-nubia-green-primary/5 border-2 border-nubia-green-primary/20 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-8 h-8 text-nubia-green-support flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-nubia-green-primary mb-3">
                    Tramitación Gratuita Incluida
                  </h3>
                  <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
                    El asesoramiento y gestión de todas las ayudas disponibles está incluido en nuestro 
                    servicio sin coste adicional. Nos encargamos de toda la documentación, solicitudes y 
                    seguimiento hasta que recibas las ayudas. Tú solo tienes que preocuparte de empezar a ahorrar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Calculation */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Calculator className="w-16 h-16 text-nubia-gold-primary mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-nubia-green-primary mb-4">
                Ejemplo Real de Ahorro con Ayudas
              </h2>
            </div>
            
            <Card className="nubia-shadow border-2 border-nubia-gold-primary/30">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold text-nubia-green-primary mb-6">Instalación Residencial Típica</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between py-3 border-b border-nubia-gray-light">
                        <span className="text-nubia-text-dark/70">Coste total instalación</span>
                        <span className="font-bold text-nubia-green-primary">7.000 €</span>
                      </div>
                      <div className="flex justify-between py-3 border-b border-nubia-gray-light">
                        <span className="text-nubia-text-dark/70">Deducción IRPF (40%)</span>
                        <span className="font-bold text-nubia-gold-primary">- 2.800 €</span>
                      </div>
                      <div className="flex justify-between py-3 border-b border-nubia-gray-light">
                        <span className="text-nubia-text-dark/70">Subvención autonómica (30%)</span>
                        <span className="font-bold text-nubia-gold-primary">- 2.100 €</span>
                      </div>
                      <div className="flex justify-between py-3 border-b border-nubia-gray-light">
                        <span className="text-nubia-text-dark/70">Bonificación ICIO (75%)</span>
                        <span className="font-bold text-nubia-gold-primary">- 400 €</span>
                      </div>
                      <div className="flex justify-between py-4 bg-nubia-green-primary/10 rounded-lg px-4 mt-4">
                        <span className="font-bold text-nubia-green-primary text-lg">Coste final real</span>
                        <span className="font-bold text-nubia-green-primary text-2xl">1.700 €</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="bg-nubia-gold-soft/10 border-2 border-nubia-gold-primary/30 rounded-2xl p-8 text-center">
                      <div className="text-6xl font-bold text-nubia-gold-primary mb-4">76%</div>
                      <div className="text-2xl font-semibold text-nubia-green-primary mb-3">Ahorro Total</div>
                      <p className="text-nubia-text-dark/70">
                        Combinando diferentes ayudas puedes reducir tu inversión inicial hasta en un 75-80%
                      </p>
                    </div>
                    <div className="mt-6 text-sm text-nubia-text-dark/60 text-center">
                      * Ejemplo orientativo. Las ayudas varían según municipio, comunidad autónoma y características de la instalación.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Quieres Saber Qué Ayudas Puedes Solicitar?
          </h2>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-nubia-white/90">
            Solicita tu estudio personalizado gratuito y te informamos de todas las ayudas disponibles en tu zona
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Solicitar Asesoramiento Gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculadora-solar">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nubia-green-primary px-10 py-7 text-lg">
                Calcular Mi Ahorro
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AyudasSubvenciones;
